/**
 * Unicode-aware text segmentation for the text renderers: user-perceived
 * characters (so ZWJ emoji, flags and combining marks never split) and line
 * break opportunities (spaces, and between ideographic words, which have none).
 */

type SegmenterLike = { segment(text: string): Iterable<{ segment: string; isWordLike?: boolean }> };

const segmenters = new Map<string, SegmenterLike | null>();

function segmenter(granularity: 'grapheme' | 'word'): SegmenterLike | null {
  if (!segmenters.has(granularity)) {
    const Ctor = (Intl as unknown as { Segmenter?: new (locale: string, opts: { granularity: string }) => SegmenterLike }).Segmenter;
    segmenters.set(granularity, Ctor ? new Ctor('und', { granularity }) : null);
  }
  return segmenters.get(granularity) ?? null;
}

/** User-perceived characters. Falls back to code points where Intl.Segmenter is missing. */
export function graphemes(text: string): string[] {
  const seg = segmenter('grapheme');
  if (!seg) return Array.from(text);
  return Array.from(seg.segment(text), (s) => s.segment);
}

const IDEOGRAPHIC = /[\p{Ideographic}\p{Script=Hiragana}\p{Script=Katakana}]/u;

/**
 * Splits a paragraph into pieces a line may break between. Whitespace stays
 * at the end of the piece it follows, so joining the pieces gives back the
 * paragraph exactly.
 */
export function breakPieces(paragraph: string): string[] {
  const seg = segmenter('word');
  const parts = seg ? Array.from(seg.segment(paragraph), (s) => s.segment) : paragraph.split(/(?<= )/);
  const pieces: string[] = [];
  let current = '';
  for (const part of parts) {
    const breakBefore =
      current !== '' && (/\s$/.test(current) ? !/^\s/.test(part) : IDEOGRAPHIC.test(part.charAt(0)) || IDEOGRAPHIC.test(current.slice(-1)));
    if (breakBefore) {
      pieces.push(current);
      current = part;
    } else {
      current += part;
    }
  }
  if (current !== '') pieces.push(current);
  return pieces;
}

export type TextTransform = 'none' | 'uppercase' | 'lowercase' | 'capitalize';

/** Locale-aware case transform. `capitalize` upper-cases the first character of every word. */
export function transformText(text: string, transform: TextTransform, locale?: string): string {
  const loc = locale || undefined;
  if (transform === 'uppercase') return text.toLocaleUpperCase(loc);
  if (transform === 'lowercase') return text.toLocaleLowerCase(loc);
  if (transform !== 'capitalize') return text;
  const seg = segmenter('word');
  if (!seg) return text.replace(/(^|\s)(\S)/gu, (_m, space: string, ch: string) => space + ch.toLocaleUpperCase(loc));
  let out = '';
  for (const s of seg.segment(text)) {
    if (s.isWordLike) {
      const [first = '', ...rest] = graphemes(s.segment);
      out += first.toLocaleUpperCase(loc) + rest.join('');
    } else {
      out += s.segment;
    }
  }
  return out;
}
